/**
 * `/` in the composer opens the saved-prompt typeahead (2026-09-17).
 *
 * A SIBLING of `useThreadMentionAutocomplete`, not a fork of it: same shape
 * (refs so the composer's handlers stay closure-stable while reading live
 * values), same read-only detection on the live Selection, same
 * `handleInput` / `handleKeyDown` contract so it drops into the composer's
 * existing chain. What differs is the detector — see `slashTrigger.ts` for why
 * `/` cannot use the mention scanner's rules — and that it needs no roster
 * load, because the prompts are already in memory.
 *
 * WHERE IT SITS IN THE KEY CHAIN, and why: the composer runs
 * `mentionAutocomplete.handleKeyDown` first, then the Tab-mention affordance,
 * then this. Mentions keep first refusal so the older surface cannot regress,
 * and the two cannot fight over a key in practice — the affordance owns Tab
 * alone, and when the mention dropdown is open it consumes the arrows and
 * Enter before this is ever asked. `suppressed` exists for the one case that
 * remains: both detectors legitimately matching at once (`/intro @ma`), where
 * only one may paint. It is resolved IN THE VIEW at render time rather than in
 * this state, which would be one render stale at exactly the wrong moment.
 *
 * Deliberately no Firebase import of any kind: this module is rendered by the
 * chat-isolation suites, and a module that reaches `firebase/auth` touches
 * global `fetch` at import time and takes the suite down before a test runs
 * (2026-09-11).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { detectPromptSlash, type ActivePromptSlash } from '../../services/promptLibrary/slashTrigger';
import { orderPrompts, type SavedPrompt, type SharedPrompt } from '../../types/savedPrompts';

/** How many rows the typeahead shows before it stops listing. */
export const SLASH_ROWS_MAX = 6;

export interface SlashRow {
  shelf: 'mine' | 'team';
  id: string;
  title: string;
  body: string;
  category: string;
  blanks: string[];
}

interface OpenState {
  open: boolean;
  rows: SlashRow[];
  activeIndex: number;
  active: ActivePromptSlash | null;
  anchorRect: { left: number; top: number; width: number } | null;
}

const CLOSED: OpenState = { open: false, rows: [], activeIndex: 0, active: null, anchorRect: null };

export interface PromptSlashController {
  open: boolean;
  rows: SlashRow[];
  activeIndex: number;
  anchorRect: { left: number; top: number; width: number } | null;
  /** Call from the composer's `onInput`. Read-only; safe to call anytime. */
  handleInput: () => void;
  /** Returns true when it consumed the key. Put AFTER the mention handlers. */
  handleKeyDown: (e: { key: string; preventDefault: () => void }) => boolean;
  /** Move the highlight to a row the pointer is over. */
  onHover: (index: number) => void;
  close: () => void;
  /**
   * Commit a row — the CLICK path. The span it replaces comes from this
   * controller's own live state rather than from the view, which has no
   * business knowing where in the draft the `/` was.
   */
  onPick: (row: SlashRow) => void;
}

export interface UsePromptSlashArgs {
  inputRef: { current: HTMLElement | null };
  /** The viewer's own prompts, already ordered by the library hook. */
  prompts: readonly SavedPrompt[];
  /** What the workspace published, or none. */
  team: readonly SharedPrompt[];
  /** Off entirely — no signed-in person, or the library is unavailable. */
  enabled: boolean;
  /** Commit: the caller replaces `active`'s span in the draft with the prompt. */
  onPick: (row: SlashRow, active: ActivePromptSlash) => void;
}

/**
 * The live caret, as the mention controller reads it: the focus TEXT NODE and
 * the offset within it. Reading the node rather than the whole composer is what
 * makes "the start of a line" detectable at all in a contentEditable whose
 * lines are separate nodes.
 */
function readCaret(el: HTMLElement | null): { text: string; caret: number } | null {
  if (!el || typeof window === 'undefined') return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const node = sel.focusNode;
  if (!node || node.nodeType !== Node.TEXT_NODE) return null;
  if (!el.contains(node)) return null;
  return { text: node.textContent ?? '', caret: sel.focusOffset };
}

/** Rank by the NAME first — what a person typing `/intro` is reaching for. */
function rank(rows: SlashRow[], query: string): SlashRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const scored: Array<{ row: SlashRow; score: number }> = [];
  for (const row of rows) {
    const title = row.title.toLowerCase();
    let score = 0;
    if (title.startsWith(q)) score += 100;
    else if (title.includes(q)) score += 50;
    if (row.category.toLowerCase().includes(q)) score += 10;
    if (score === 0 && row.body.toLowerCase().includes(q)) score += 1;
    if (score > 0) scored.push({ row, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((s) => s.row);
}

export function usePromptSlashAutocomplete(args: UsePromptSlashArgs): PromptSlashController {
  const { inputRef } = args;
  const [state, setState] = useState<OpenState>(CLOSED);

  const enabledRef = useRef(args.enabled);
  enabledRef.current = args.enabled;
  const stateRef = useRef(state);
  stateRef.current = state;
  const onPickRef = useRef(args.onPick);
  onPickRef.current = args.onPick;

  /**
   * Both shelves, flattened once. The person's own first — those are the ones
   * they reach for — then the workspace's, by name, which is all a shared
   * prompt can be ordered by (it carries no per-person use stamp, by design).
   */
  const all = useMemo<SlashRow[]>(() => {
    const mine: SlashRow[] = orderPrompts(args.prompts).map((p) => ({
      shelf: 'mine', id: p.id, title: p.title, body: p.body, category: p.category, blanks: p.blanks,
    }));
    const team: SlashRow[] = [...args.team]
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((p) => ({
        shelf: 'team', id: p.id, title: p.title, body: p.body, category: p.category, blanks: p.blanks,
      }));
    return [...mine, ...team];
  }, [args.prompts, args.team]);
  const allRef = useRef(all);
  allRef.current = all;

  const close = useCallback(() => {
    setState((s) => (s.open ? CLOSED : s));
  }, []);

  const recompute = useCallback(() => {
    if (!enabledRef.current) {
      if (stateRef.current.open) close();
      return;
    }
    const el = inputRef.current;
    const caret = readCaret(el);
    if (!caret) { close(); return; }
    const active = detectPromptSlash(caret.text, caret.caret);
    if (!active) { close(); return; }
    const rows = rank(allRef.current, active.query).slice(0, SLASH_ROWS_MAX);
    // NOTHING TO OFFER MEANS CLOSED. A typeahead showing "no matches" over the
    // composer is a panel in the way of somebody who is plainly typing prose
    // that begins with a slash.
    if (rows.length === 0) { close(); return; }
    const rect = el?.getBoundingClientRect();
    setState((prev) => ({
      open: true,
      rows,
      // Keep the highlight when it is still in range; otherwise back to the top.
      activeIndex: prev.open && prev.activeIndex < rows.length ? prev.activeIndex : 0,
      active,
      anchorRect: rect ? { left: rect.left, top: rect.top, width: rect.width } : prev.anchorRect,
    }));
  }, [close, inputRef]);

  const handleInput = useCallback(() => {
    if (!enabledRef.current) return;
    recompute();
  }, [recompute]);

  const handleKeyDown = useCallback((e: { key: string; preventDefault: () => void }): boolean => {
    const s = stateRef.current;
    if (!s.open || s.rows.length === 0) return false;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setState((p) => ({ ...p, activeIndex: (p.activeIndex + 1) % p.rows.length }));
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setState((p) => ({ ...p, activeIndex: (p.activeIndex - 1 + p.rows.length) % p.rows.length }));
      return true;
    }
    if (e.key === 'Enter') {
      const row = s.rows[Math.min(s.activeIndex, s.rows.length - 1)];
      if (!row || !s.active) return false;
      e.preventDefault();
      close();
      onPickRef.current(row, s.active);
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return true;
    }
    // Tab is NOT consumed: the mention affordance owns it, and it has already
    // had its refusal by the time this runs.
    return false;
  }, [close]);

  const onHover = useCallback((index: number) => {
    setState((p) => (p.open && index >= 0 && index < p.rows.length && index !== p.activeIndex
      ? { ...p, activeIndex: index }
      : p));
  }, []);

  const onPick = useCallback((row: SlashRow) => {
    const active = stateRef.current.active;
    if (!active) return;
    close();
    onPickRef.current(row, active);
  }, [close]);

  return {
    open: state.open,
    rows: state.rows,
    activeIndex: state.activeIndex,
    anchorRect: state.anchorRect,
    handleInput,
    handleKeyDown,
    onHover,
    close,
    onPick,
  };
}
